import React from "react";
import { generatedGuides } from "../../../guides/manifest.generated";
import { array as arrayCodec } from "../../../../../shared/src/codecs/types/array";
import { required } from "../../../../../shared/src/codecs/types/required";
import { uuid } from "../../../../../shared/src/codecs/types/uuid";
import { string } from "../../../../../shared/src/codecs/types/string";
import { integer } from "../../../../../shared/src/codecs/types/integer";
import { TTypeOfCodec } from "../../../../../shared/src/codecs/codec";
import { TAdminUserEditable } from "../../../../../domain/codecs/formEditable/AdminUserEditable";
import { CRMDropdownComponent } from "../../CRMDropdownComponent/CRMDropdownComponent";
import { IOption } from "../../../hooks/UseDropdown";

const PAGE_SIZE = 50;

const AdminReadRow = required({
    id: uuid(),
    user_id: uuid(),
    user_first_name: string(),
    user_last_name: string(),
    product: string(),
    slug: string(),
    content_hash: string(),
    read_at: string(),
});

type TAdminReadRow = TTypeOfCodec<typeof AdminReadRow>;

const AdminReadsResponse = required({
    rows: arrayCodec(AdminReadRow),
    total: integer(),
});

type TFilterValue = string;
const ALL: TFilterValue = "";

const fetchReads = async (filters: {
    userId: TFilterValue;
    guideKey: TFilterValue;
    offset: number;
}): Promise<TTypeOfCodec<typeof AdminReadsResponse>> => {
    const params = new URLSearchParams();
    if (filters.userId !== ALL) params.set("user_id", filters.userId);
    if (filters.guideKey !== ALL) {
        const [product, slug] = filters.guideKey.split("/");
        if (product) params.set("product", product);
        if (slug) params.set("slug", slug);
    }
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(filters.offset));

    const res = await fetch(`${env.REACT_APP_API_URL}/web/guides/reads/admin?${params.toString()}`, {
        credentials: "include",
        headers: { accept: "application/json", "content-type": "application/json" },
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const raw: unknown = await res.json();
    const decoded = AdminReadsResponse.decode(raw);
    if (decoded._tag !== "Right") throw new Error("admin reads response failed validation");
    return decoded.right;
};

const formatDate = (iso: string): string => {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return iso;
    return d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const sortedUserOptions = (users: Array<TAdminUserEditable>): Array<IOption<TFilterValue>> => {
    const live = users
        .filter((u) => u.edited.deleted_at === null)
        .slice()
        .sort((a, b) => {
            const aKey = `${a.edited.last_name} ${a.edited.first_name}`.toLowerCase();
            const bKey = `${b.edited.last_name} ${b.edited.first_name}`.toLowerCase();
            return aKey.localeCompare(bKey);
        });
    return [
        { value: ALL, label: "All users" },
        ...live.map((u) => ({
            value: u.edited.id,
            label: `${u.edited.first_name} ${u.edited.last_name}`.trim() || u.edited.staff_email || u.edited.id,
        })),
    ];
};

const guideOptions = (): Array<IOption<TFilterValue>> => [
    { value: ALL, label: "All guides" },
    ...generatedGuides.map((g) => ({
        value: `${g.product}/${g.slug}`,
        label: `${g.product} / ${g.title}`,
    })),
];

const VersionPill = (props: { matchesCurrent: boolean; hash: string }): JSX.Element => (
    <>
        {props.matchesCurrent
            ? <span className="crm-guides-admin-reads__pill crm-guides-admin-reads__pill--current">current</span>
            : <span className="crm-guides-admin-reads__pill crm-guides-admin-reads__pill--stale">earlier</span>
        }
        <span className="crm-guides-admin-reads__hash">{props.hash.slice(0, 8)}</span>
    </>
);

const ReadsTable = (props: { rows: Array<TAdminReadRow> }): JSX.Element => {
    if (props.rows.length === 0) {
        return <p className="crm-guides-admin-reads__empty">No reads match these filters.</p>;
    }
    return (
        <table className="crm-guides-admin-reads__table">
            <thead>
                <tr>
                    <th>User</th>
                    <th>Guide</th>
                    <th>Version</th>
                    <th>Read at</th>
                </tr>
            </thead>
            <tbody>
                {props.rows.map((r) => {
                    const guide = generatedGuides.find((g) => g.product === r.product && g.slug === r.slug);
                    const currentHash = guide ? guide.content_hash : "";
                    return (
                        <tr key={r.id}>
                            <td>{`${r.user_first_name} ${r.user_last_name}`.trim() || "—"}</td>
                            <td>{guide ? guide.title : `${r.product}/${r.slug}`}</td>
                            <td><VersionPill matchesCurrent={r.content_hash === currentHash} hash={r.content_hash} /></td>
                            <td>{formatDate(r.read_at)}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export const CRMGuidesAdminReads = (props: { adminUsers: Array<TAdminUserEditable> }): JSX.Element => {
    const [userId, setUserId] = React.useState<TFilterValue>(ALL);
    const [guideKey, setGuideKey] = React.useState<TFilterValue>(ALL);
    const [rows, setRows] = React.useState<Array<TAdminReadRow>>([]);
    const [total, setTotal] = React.useState<number>(0);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);

    const userOptions = React.useMemo(() => sortedUserOptions(props.adminUsers), [props.adminUsers]);
    const guides = React.useMemo(() => guideOptions(), []);

    const load = React.useCallback(async (filters: { userId: TFilterValue; guideKey: TFilterValue; offset: number; append: boolean }): Promise<void> => {
        setLoading(true);
        setError(null);
        try {
            const result = await fetchReads(filters);
            setTotal(result.total);
            setRows((prev) => filters.append ? [...prev, ...result.rows] : result.rows);
        } catch (e) {
            console.error("[guides] failed to load admin reads", e);
            setError(e instanceof Error ? e.message : "Couldn't load reads");
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        load({ userId, guideKey, offset: 0, append: false });
    }, [userId, guideKey, load]);

    const handleLoadMore = (): void => {
        load({ userId, guideKey, offset: rows.length, append: true });
    };

    const hasMore = rows.length < total;

    return (
        <div className="crm-guides-admin-reads">
            <div className="crm-guides-admin-reads__filters">
                <div className="crm-guides-admin-reads__filter">
                    <span className="crm-guides-admin-reads__filter-label">User</span>
                    <CRMDropdownComponent
                        options={userOptions}
                        value={userId}
                        displayError={false}
                        onChange={(value) => setUserId(value)}
                    />
                </div>
                <div className="crm-guides-admin-reads__filter">
                    <span className="crm-guides-admin-reads__filter-label">Guide</span>
                    <CRMDropdownComponent
                        options={guides}
                        value={guideKey}
                        displayError={false}
                        onChange={(value) => setGuideKey(value)}
                    />
                </div>
            </div>

            <p className="crm-guides-admin-reads__meta">
                {loading && rows.length === 0
                    ? "Loading..."
                    : `Showing ${rows.length} of ${total} read${total === 1 ? "" : "s"}`
                }
            </p>

            <ReadsTable rows={rows} />

            {hasMore && (
                <div className="crm-guides-admin-reads__load-more-row">
                    <button
                        type="button"
                        className="crm-guides-admin-reads__button"
                        onClick={handleLoadMore}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : `Load next ${Math.min(PAGE_SIZE, total - rows.length)}`}
                    </button>
                </div>
            )}

            {error && <p className="crm-guides-admin-reads__error">{error}</p>}
        </div>
    );
};
